import { SupportFaqDialogComponent } from '../../dialogs/support-faq-dialog/support-faq-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Backlink } from '@core/interfaces/backlink.model';
import { MatDialog } from '@angular/material/dialog';
import { AdditionalInfo } from '../../../models/additional-info.model';
import { DropdownMenuItem, xtuiButtonStyle } from '@xt/ui-kit';
import { PatientRepresentation } from '@xt/domain-model';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'xt-portal-page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  standalone: false,
})
export class PageHeaderComponent {
  @Input() backlink: Backlink;
  @Input() title: string;
  @Input() tooltip: string;
  @Input() dataCy: string = '';
  @Input() tabSupportTopic: string;
  @Input() additionalInfo: AdditionalInfo;
  @Input() additionalText: any;
  @Input() hasAction: boolean = false;
  @Input() hasExtraAction: boolean = false;
  @Input() hasSupportButton: boolean = false;
  @Input() isDropdown: boolean = false;
  @Input() disableInfoClick: boolean = false;
  @Input() helpIcon: boolean = false;
  @Input() isActionDisabled: boolean = false;
  @Input() actionTooltip: string;
  @Input() actionText: string;
  @Input() extraActionText: string;
  @Input() actionIcon: string;
  @Input() actionTextAlign: 'center' | 'left' | 'right' = 'center';
  @Input() actionIconPosition: 'left' | 'right' = 'left';
  @Input() actionColor: string | 'primary' | 'accent' | 'warn' = 'primary';
  @Input() actionStyle: xtuiButtonStyle;
  @Input() width: 'normal' | 'small' | 'medium' | 'large' = 'normal';
  @Input() dropdownItems: DropdownMenuItem[] = [];
  @Input() representativeData:
    | PatientRepresentation
    | PatientRepresentation[]
    | null;

  @Output() onClickEvent: EventEmitter<any> = new EventEmitter();
  @Output() backlinkClick: EventEmitter<any> = new EventEmitter();

  @ViewChild('trigger') trigger: any;

  public isOpen: boolean = false;

  constructor(
    public dialog: MatDialog,
    private router: Router,
    public route: ActivatedRoute,
  ) {}

  /**
   * Type guard to check if representativeData is an array
   */
  private isRepresentativeArray(
    data: PatientRepresentation | PatientRepresentation[] | null,
  ): data is PatientRepresentation[] {
    return Array.isArray(data);
  }

  /**
   * Gets the representative data as an array
   */
  get representativeDataArray(): PatientRepresentation[] {
    if (!this.representativeData) {
      return [];
    }
    return this.isRepresentativeArray(this.representativeData)
      ? this.representativeData
      : [this.representativeData];
  }

  /**
   * Checks if there is any representative data
   */
  get hasRepresentativeData(): boolean {
    return this.representativeDataArray.length > 0;
  }

  /**
   * Gets the first representative
   */
  get firstRepresentative(): PatientRepresentation {
    return (
      this.representativeDataArray?.length && this.representativeDataArray[0]
    );
  }


  /**
   * Gets the representative name with title
   */
  get representativeName(): string {
    if (!this.firstRepresentative) return '';

    const nameDetails = this.firstRepresentative.nameDetails;
    const title = nameDetails?.title ? `${nameDetails.title} ` : '';
    const name = this.firstRepresentative.name || '';

    return `${title}${name}`.trim();
  }

  /**
   * Gets the count of additional representatives (excluding the first one)
   */
  get additionalRepresentativesCount(): number {
    const count = this.representativeDataArray.length - 1;
    return count > 0 ? count : 0;
  }

  /**
   * Checks if there are multiple representatives
   */
  get hasMultipleRepresentatives(): boolean {
    return this.representativeDataArray.length > 1;
  }

  public onClickHandler(methodName?: string): void {
    this.onClickEvent.emit(methodName || null);
  }

  /**
   * opens the support dialog window
   */
  public openInfoDialog(openDialog?: boolean): void {
    if (!this.disableInfoClick || openDialog) {
      this.dialog.open(SupportFaqDialogComponent, {
        width: '880px',
        maxHeight: '90dvh',
        data: {
          topic:
            this.tabSupportTopic || this.route?.snapshot?.data?.supportTopic,
          type: 'info',
        },
      });
    }
  }

  public navigate(): void {
    if (this.backlink.url) {
      this.router.navigate([this.backlink.url]);
    } else {
      this.backlinkClick.emit();
    }
  }
}
